import React, { useState, useEffect, useCallback } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import { AlertTriangle, Eye, RefreshCw, Unlock, X, Search } from 'lucide-react';


// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatEuro = (cents) => {
  if (cents == null) return '€0,00';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })?.format(cents / 100);
};

const formatDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts)?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const daysSince = (ts) => {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts)?.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => (
  <div
    className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    }`}
  >
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-80"><X size={16} /></button>
  </div>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────

const BillingStatusBadge = ({ status }) => {
  if (status === 'past_due') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
        In ritardo
      </span>
    );
  }
  if (status === 'blocked') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
        Bloccato
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
      {status}
    </span>
  );
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────

const ConfirmUnblockModal = ({ professional, onClose, onConfirm, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
          <Unlock size={20} className="text-orange-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Sblocca professionista</h3>
          <p className="text-sm text-gray-600 mt-1">
            Vuoi sbloccare manualmente <strong>{professional?.email}</strong>? Il professionista potrà inviare nuovi preventivi ma il saldo non pagato resterà a debito.
          </p>
        </div>
      </div>
      <div className="flex gap-3 justify-end mt-6">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Annulla
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? 'Sblocco in corso...' : 'Sblocca'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Invoice Drawer ───────────────────────────────────────────────────────────

const InvoiceDrawer = ({ professional, invoices, onClose, onRetryInvoice, retryingId }) => {
  const ITALIAN_MONTHS = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Fatture fallite</h2>
            <p className="text-sm text-gray-500 mt-0.5">{professional?.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Profile summary */}
        <div className="px-6 py-4 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-700 font-bold text-lg">
                {professional?.email?.[0]?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{professional?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <BillingStatusBadge status={professional?.billing_status} />
                <span className="text-xs text-gray-500">ID: {professional?.id?.slice(0, 8)}…</span>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {invoices?.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">Nessuna fattura fallita trovata.</p>
          ) : (
            <div className="space-y-4">
              {invoices?.map((inv) => (
                <div key={inv?.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:border-gray-300 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {ITALIAN_MONTHS?.[inv?.period_month - 1]} {inv?.period_year}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          Fallita
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                        <span><strong>Importo:</strong> {formatEuro(inv?.amount_cents)}</span>
                        <span><strong>Tentativi:</strong> {inv?.retry_count ?? 0}</span>
                        {inv?.failure_reason && (
                          <span className="col-span-2"><strong>Motivo:</strong> {inv?.failure_reason}</span>
                        )}
                        {inv?.failed_at && (
                          <span><strong>Fallita il:</strong> {formatDate(inv?.failed_at)}</span>
                        )}
                        {inv?.next_retry_at && (
                          <span><strong>Prossimo retry:</strong> {formatDate(inv?.next_retry_at)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onRetryInvoice(inv?.id)}
                      disabled={retryingId === inv?.id}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <RefreshCw size={13} className={retryingId === inv?.id ? 'animate-spin' : ''} />
                      Riprova ora
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminInsolvenzeManagement = () => {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ pastDue: 0, blocked: 0, totalRecoverable: 0 });

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');

  // Drawer
  const [drawerProfessional, setDrawerProfessional] = useState(null);
  const [drawerInvoices, setDrawerInvoices] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  // Unblock modal
  const [unblockTarget, setUnblockTarget] = useState(null);
  const [unblockLoading, setUnblockLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ─── Fetch data ─────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch users with billing_status in past_due or blocked
      const { data: users, error: usersError } = await supabase
        ?.from('users')
        ?.select('id, email, billing_status, payment_blocked_at')
        ?.in('billing_status', ['past_due', 'blocked'])
        ?.order('email', { ascending: true });

      if (usersError) throw usersError;

      // Fetch failed invoices aggregated per user
      const { data: invoices, error: invError } = await supabase
        ?.from('professional_invoices')
        ?.select('professional_id, amount_cents, failed_at, status')
        ?.eq('status', 'failed');

      if (invError) throw invError;

      // Build per-user invoice stats
      const invoiceMap = {};
      invoices?.forEach((inv) => {
        if (!invoiceMap?.[inv?.professional_id]) {
          invoiceMap[inv?.professional_id] = { count: 0, totalCents: 0, firstFailed: null };
        }
        invoiceMap[inv?.professional_id].count += 1;
        invoiceMap[inv?.professional_id].totalCents += inv?.amount_cents || 0;
        const fa = inv?.failed_at ? new Date(inv?.failed_at) : null;
        if (fa && (!invoiceMap?.[inv?.professional_id]?.firstFailed || fa < invoiceMap?.[inv?.professional_id]?.firstFailed)) {
          invoiceMap[inv?.professional_id].firstFailed = fa;
        }
      });

      const enriched = (users || [])?.map((u) => ({
        ...u,
        failedCount: invoiceMap?.[u?.id]?.count || 0,
        failedTotalCents: invoiceMap?.[u?.id]?.totalCents || 0,
        firstFailed: invoiceMap?.[u?.id]?.firstFailed || null,
      }));

      setProfessionals(enriched);

      // KPIs
      const pastDueCount = enriched?.filter((u) => u?.billing_status === 'past_due')?.length;
      const blockedCount = enriched?.filter((u) => u?.billing_status === 'blocked')?.length;
      const totalRecoverable = invoices?.reduce((acc, inv) => acc + (inv?.amount_cents || 0), 0);
      setKpis({ pastDue: pastDueCount, blocked: blockedCount, totalRecoverable });
    } catch (err) {
      console.error('Error fetching insolvenze data:', err);
      showToast('Errore nel caricamento dei dati', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Open drawer ─────────────────────────────────────────────────────────────

  const openDrawer = async (professional) => {
    setDrawerProfessional(professional);
    setDrawerInvoices([]);
    setDrawerLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('professional_invoices')
        ?.select('id, amount_cents, status, failure_reason, retry_count, next_retry_at, failed_at, period_year, period_month')
        ?.eq('professional_id', professional?.id)
        ?.eq('status', 'failed')
        ?.order('failed_at', { ascending: false });

      if (error) throw error;
      setDrawerInvoices(data || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      showToast('Errore nel caricamento delle fatture', 'error');
    } finally {
      setDrawerLoading(false);
    }
  };

  // ─── Force retry ─────────────────────────────────────────────────────────────

  const handleForceRetry = async (invoiceId = null) => {
    if (invoiceId) setRetryingId(invoiceId);
    try {
      await supabase?.functions?.invoke('retry-failed-invoices');
      await sleep(2000);
      await fetchData();
      // Refresh drawer invoices if open
      if (drawerProfessional) {
        const { data } = await supabase
          ?.from('professional_invoices')
          ?.select('id, amount_cents, status, failure_reason, retry_count, next_retry_at, failed_at, period_year, period_month')
          ?.eq('professional_id', drawerProfessional?.id)
          ?.eq('status', 'failed')
          ?.order('failed_at', { ascending: false });
        setDrawerInvoices(data || []);
      }
      showToast('Retry forzato avviato — controlla in qualche minuto');
    } catch (err) {
      console.error('Retry error:', err);
      showToast('Errore durante il retry', 'error');
    } finally {
      setRetryingId(null);
    }
  };

  // ─── Manual unblock ───────────────────────────────────────────────────────────

  const handleUnblock = async () => {
    if (!unblockTarget) return;
    setUnblockLoading(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { error } = await supabase
        ?.from('users')
        ?.update({ billing_status: 'active', payment_blocked_at: null })
        ?.eq('id', unblockTarget?.id);

      if (error) throw error;

      // Log admin action
      try {
        await supabase?.from('audit_logs')?.insert({
          admin_id: user?.id,
          action: 'manual_unblock_professional',
          entity_type: 'user',
          entity_id: unblockTarget?.id,
          details: { email: unblockTarget?.email }
        });
      } catch (logErr) {
        console.warn('Audit log failed:', logErr);
      }

      showToast('Professionista sbloccato');
      setUnblockTarget(null);
      await fetchData();
    } catch (err) {
      console.error('Unblock error:', err);
      showToast('Errore durante lo sblocco', 'error');
    } finally {
      setUnblockLoading(false);
    }
  };

  // ─── Filtered list ────────────────────────────────────────────────────────────

  const filtered = professionals?.filter((p) => {
    const matchStatus = statusFilter === 'all' || p?.billing_status === statusFilter;
    const matchEmail = !emailSearch || p?.email?.toLowerCase()?.includes(emailSearch?.toLowerCase());
    return matchStatus && matchEmail;
  });

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <AdminDashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Gestione Insolvenze</h1>
        <p className="text-gray-500 mt-1">Professionisti con pagamenti in ritardo o bloccati</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertTriangle size={20} className="text-yellow-600" />
            </div>
            <span className="text-sm font-medium text-gray-600">Past due</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{kpis?.pastDue}</p>
          <p className="text-xs text-gray-500 mt-1">professionisti in ritardo</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <span className="text-sm font-medium text-gray-600">Bloccati</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{kpis?.blocked}</p>
          <p className="text-xs text-gray-500 mt-1">professionisti bloccati</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-700 font-bold text-sm">€</span>
            </div>
            <span className="text-sm font-medium text-gray-600">Importo totale recuperabile</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatEuro(kpis?.totalRecoverable)}</p>
          <p className="text-xs text-gray-500 mt-1">da fatture fallite</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Status filter */}
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'Tutti' },
              { value: 'past_due', label: 'Past due' },
              { value: 'blocked', label: 'Bloccati' },
            ]?.map((opt) => (
              <button
                key={opt?.value}
                onClick={() => setStatusFilter(opt?.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === opt?.value
                    ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {opt?.label}
              </button>
            ))}
          </div>

          {/* Email search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per email..."
              value={emailSearch}
              onChange={(e) => setEmailSearch(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filtered?.length === 0 ? (
          <div className="text-center py-16">
            <AlertTriangle size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">Nessun professionista trovato</p>
            <p className="text-gray-400 text-sm mt-1">
              {statusFilter !== 'all' || emailSearch ?'Prova a modificare i filtri' :'Nessun professionista con pagamenti in ritardo o bloccati'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email professionista</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stato</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">N fatture fallite</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Importo dovuto</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Primo fallimento</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Giorni insolvenza</th>
                  <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered?.map((p) => (
                  <tr key={p?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">{p?.email}</span>
                    </td>
                    <td className="px-6 py-4">
                      <BillingStatusBadge status={p?.billing_status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm text-gray-900 font-medium">{p?.failedCount}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-semibold text-red-700">{formatEuro(p?.failedTotalCents)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{formatDate(p?.firstFailed)}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm text-gray-900">
                        {p?.firstFailed ? `${daysSince(p?.firstFailed)} gg` : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {/* View drawer */}
                        <button
                          onClick={() => openDrawer(p)}
                          title="Visualizza fatture"
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Eye size={16} />
                        </button>

                        {/* Force retry */}
                        <button
                          onClick={() => handleForceRetry()}
                          title="Forza retry"
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <RefreshCw size={16} />
                        </button>

                        {/* Manual unblock — only if blocked */}
                        {p?.billing_status === 'blocked' && (
                          <button
                            onClick={() => setUnblockTarget(p)}
                            title="Sblocca manualmente"
                            className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          >
                            <Unlock size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice Drawer */}
      {drawerProfessional && (
        <InvoiceDrawer
          professional={drawerProfessional}
          invoices={drawerLoading ? [] : drawerInvoices}
          onClose={() => { setDrawerProfessional(null); setDrawerInvoices([]); }}
          onRetryInvoice={handleForceRetry}
          retryingId={retryingId}
        />
      )}

      {/* Unblock Confirmation Modal */}
      {unblockTarget && (
        <ConfirmUnblockModal
          professional={unblockTarget}
          onClose={() => setUnblockTarget(null)}
          onConfirm={handleUnblock}
          loading={unblockLoading}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      )}
    </AdminDashboardLayout>
  );
};

export default AdminInsolvenzeManagement;
